export interface RasterDimensions {
  width: number
  height: number
  format: 'jpeg' | 'png' | 'webp'
}

const byteAt = (bytes: Uint8Array, offset: number): number => bytes[offset] ?? 0
const readU16BE = (bytes: Uint8Array, offset: number): number => (byteAt(bytes, offset) << 8) | byteAt(bytes, offset + 1)
const readU24LE = (bytes: Uint8Array, offset: number): number => byteAt(bytes, offset) | (byteAt(bytes, offset + 1) << 8) | (byteAt(bytes, offset + 2) << 16)
const readU32LE = (bytes: Uint8Array, offset: number): number => (byteAt(bytes, offset) | (byteAt(bytes, offset + 1) << 8) | (byteAt(bytes, offset + 2) << 16) | (byteAt(bytes, offset + 3) << 24)) >>> 0
const readU32BE = (bytes: Uint8Array, offset: number): number => ((byteAt(bytes, offset) << 24) | (byteAt(bytes, offset + 1) << 16) | (byteAt(bytes, offset + 2) << 8) | byteAt(bytes, offset + 3)) >>> 0

const isPng = (bytes: Uint8Array): boolean => bytes.length >= 24
  && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
  && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a

const isJpeg = (bytes: Uint8Array): boolean => bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8
const isWebp = (bytes: Uint8Array): boolean => bytes.length >= 20
  && String.fromCharCode(...bytes.subarray(0, 4)) === 'RIFF'
  && String.fromCharCode(...bytes.subarray(8, 12)) === 'WEBP'

function inspectJpeg(bytes: Uint8Array): RasterDimensions | undefined {
  if (!isJpeg(bytes)) return undefined
  const sofMarkers = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf])
  let offset = 2
  while (offset + 3 < bytes.length) {
    while (offset < bytes.length && bytes[offset] !== 0xff) offset += 1
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1
    if (offset >= bytes.length) break
    const marker = byteAt(bytes, offset)
    offset += 1
    if (marker === 0xd9 || marker === 0xda) break
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue
    if (offset + 2 > bytes.length) break
    const segmentLength = readU16BE(bytes, offset)
    if (segmentLength < 2 || offset + segmentLength > bytes.length) break
    if (sofMarkers.has(marker) && segmentLength >= 7) {
      const height = readU16BE(bytes, offset + 3)
      const width = readU16BE(bytes, offset + 5)
      if (width > 0 && height > 0) return { width, height, format: 'jpeg' }
      return undefined
    }
    offset += segmentLength
  }
  return undefined
}

function inspectWebp(bytes: Uint8Array): RasterDimensions | undefined {
  if (!isWebp(bytes)) return undefined
  let offset = 12
  while (offset + 8 <= bytes.length) {
    const type = String.fromCharCode(...bytes.subarray(offset, offset + 4))
    const chunkLength = readU32LE(bytes, offset + 4)
    const dataOffset = offset + 8
    if (chunkLength > bytes.length - dataOffset) return undefined

    if (type === 'VP8X' && chunkLength >= 10) {
      const width = readU24LE(bytes, dataOffset + 4) + 1
      const height = readU24LE(bytes, dataOffset + 7) + 1
      return { width, height, format: 'webp' }
    }

    if (type === 'VP8L' && chunkLength >= 5 && bytes[dataOffset] === 0x2f) {
      const b1 = byteAt(bytes, dataOffset + 1)
      const b2 = byteAt(bytes, dataOffset + 2)
      const b3 = byteAt(bytes, dataOffset + 3)
      const b4 = byteAt(bytes, dataOffset + 4)
      const width = 1 + b1 + ((b2 & 0x3f) << 8)
      const height = 1 + ((b2 & 0xc0) >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10)
      return { width, height, format: 'webp' }
    }

    if (type === 'VP8 ' && chunkLength >= 10
      && bytes[dataOffset + 3] === 0x9d && bytes[dataOffset + 4] === 0x01 && bytes[dataOffset + 5] === 0x2a) {
      const width = (byteAt(bytes, dataOffset + 6) | (byteAt(bytes, dataOffset + 7) << 8)) & 0x3fff
      const height = (byteAt(bytes, dataOffset + 8) | (byteAt(bytes, dataOffset + 9) << 8)) & 0x3fff
      if (width > 0 && height > 0) return { width, height, format: 'webp' }
      return undefined
    }

    offset = dataOffset + chunkLength + (chunkLength % 2)
  }
  return undefined
}

export function inspectRasterDimensionsBytes(bytes: Uint8Array): RasterDimensions | undefined {
  if (isPng(bytes)) {
    const width = readU32BE(bytes, 16)
    const height = readU32BE(bytes, 20)
    return width > 0 && height > 0 ? { width, height, format: 'png' } : undefined
  }
  return inspectJpeg(bytes) ?? inspectWebp(bytes)
}

export async function inspectRasterDimensions(file: Blob, maxHeaderBytes = 2 * 1024 * 1024): Promise<RasterDimensions | undefined> {
  const bytes = new Uint8Array(await file.slice(0, Math.min(file.size, maxHeaderBytes)).arrayBuffer())
  return inspectRasterDimensionsBytes(bytes)
}
